import React, { useEffect } from "react";
import { Container, Table } from "react-bootstrap";

const StudentsScreen = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const subjectCellStyles = {
    "English (Writing)": { backgroundColor: "#eef4ff" },
    Mathematics: { backgroundColor: "#eaf7ff" },
    Science: { backgroundColor: "#ecfdf3" },
    Humanities: { backgroundColor: "#fff6e5" },
    Geography: { backgroundColor: "#fff0f5" },
    History: { backgroundColor: "#f3f0ff" },
  };

  const renderModuleRow = (subject, moduleName) => {
    const style = subjectCellStyles[subject] || {};

    return (
      <tr>
        <td style={style}>{subject}</td>
        <td style={style}>{moduleName}</td>
      </tr>
    );
  };

  return (
    <Container className="page-shell">
      <section className="page-header">
        <h2 className="page-title">For Students</h2>
        <p className="page-subtitle fst-italic">Prepare for Success in Australian Secondary Schools</p>
      </section>

      <p>
        Transitioning into Australian secondary schooling can be challenging,
        especially for students learning English as an Additional Language
        (EAL). Our asynchronous online academic language learning materials are
        designed to bridge that gap, giving students the confidence, skills,
        and academic English they need to succeed.
      </p>

      <p>
        These materials have been designed and developed by an Australian EAL
        specialist teacher with 35 years experience teaching across the upper
        primary, middle, and high school curriculum. The program reflects:
      </p>

      <ul className="content-list">
        <li>
          Deep knowledge of Australian Curriculum and relevant classroom
          academic language needs
        </li>
        <li>Long-term experience working with EAL learners from Years 6-10</li>
        <li>
          Practical strategies proven to work both in schools and during
          AcLangEDGE live online modules
        </li>
      </ul>

      <h4 className="pt-4">Testimonials</h4>
      <hr />

      <blockquote className="fst-italic">
        "As the year is coming to an end, we would like to express our sincere
        thanks for all the support you have given to (name withheld) in the
        half past year. We truly appreciate the accessibility and relevance of
        your learning materials to his learning. (name withheld) has made great
        progress this year, and we know that your clear teaching materials have
        played a big part in his growth."
      </blockquote>

      <blockquote className="fst-italic">
        "Mr. Hudson, I have just received my school report for Semester 2, my
        first 6 months in school in Australia. I have passed all my subjects.
        Thank you very much for your great teaching resources in Science,
        English and Humanities."
      </blockquote>

      <h4 className="pt-4">Why Our Program Works</h4>
      <hr />

      <h5><span className="text-success me-2">&#10003;</span>Aligned with the latest version of the Australian Curriculum</h5>
      <p>
        Our materials explicitly target the academic language demands of
        Australian secondary schools as prescribed in the Australian
        Curriculum: subject-specific vocabulary, classroom discourse, and
        relevant reading, listening, and viewing comprehension.
      </p>

      <h5><span className="text-success me-2">&#10003;</span>Flexible, Anytime Access</h5>
      <p>
        Students can learn at their own pace, anytime and anywhere. This makes
        the program ideal for busy students and families seeking structured
        support beyond school hours. It is suitable for use prior to arrival at
        school in Australia, or after having commenced school in Australia as
        supplementary learning materials, and is particularly popular during
        school holiday periods to prepare for upcoming subject content.
      </p>

      <h5><span className="text-success me-2">&#10003;</span>Focus on Subject-Specific Academic English, Not Just General English</h5>
      <p>We go beyond everyday communication to develop:</p>
      <ul className="content-list">
        <li>Year-level and subject-specific academic vocabulary development</li>
        <li>
          Year-level and subject-specific academic reading comprehension skills
        </li>
        <li>Note-taking and information processing strategies</li>
        <li>Structured writing skills for various text types</li>
        <li>
          Listening and viewing comprehension skills for understanding teacher
          instructions and classroom explanations
        </li>
      </ul>

      <h5><span className="text-success me-2">&#10003;</span>Designed for Independent Learning</h5>
      <p>
        Clear explanations, scaffolded tasks, model texts, and self-check
        activities empower students to take ownership of their learning without
        requiring constant teacher supervision.
      </p>

      <h5><span className="text-success me-2">&#10003;</span>Smooth Transition into Secondary Schooling</h5>
      <p>
        By practising the language of English, Mathematics, Science, and
        Humanities in context in advance, students arrive at their new school
        better prepared, more confident, and ready to engage with academic
        content from day one.
      </p>

      <h4 className="pt-4">Ideal For</h4>
      <hr />
      <ul className="content-list">
        <li>EAL students preparing to enter Years 5-10 in Australia</li>
        <li>International students transitioning into Australian schools</li>
        <li>Schools seeking scalable EAL support beyond the classroom</li>
        <li>Families wanting targeted academic English preparation</li>
      </ul>

      <h4 className="pt-4">The Result</h4>
      <hr />
      <p>
        Students who are confident, classroom-ready, and able to demonstrate
        their learning because language is no longer a barrier to academic
        success.
      </p>

      <p>Upon completion of the course materials, students will:</p>
      <ul className="content-list">
        <li>
          Enter Australian classrooms with greater confidence and academic
          language knowledge
        </li>
        <li>
          Understand how English is used across and within core subjects
        </li>
        <li>
          Read and respond to academic texts across the curriculum with success
        </li>
        <li>Comprehend teacher talk with more confidence</li>
        <li>
          Write well-structured responses across various text types commonly
          taught at school
        </li>
      </ul>

      <p>
        Give EAL students the academic language foundation they need for
        Australian secondary school on their schedule, at their pace.
      </p>

      <h4 className="pt-4">Subscription and Inclusions</h4>
      <hr />
      <p>
        A 3-month subscription is priced at AUD $888 per family. You receive 11
        modules of work across English Writing, Science, Mathematics, and
        Humanities.
      </p>
      <p>
        Each module can be completed by students in around 3-5 hours, and
        includes vocabulary development activities, listening and viewing
        comprehension tasks, reading comprehension tasks, and subject-related
        scaffolded writing tasks.
      </p>

      <h4 className="pt-4">Module Coverage by Year Level</h4>
      <hr />

      <h5>Year 5-6</h5>
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Subject Area</th>
              <th>Year 5-6 Academic Language Module Name</th>
            </tr>
          </thead>
          <tbody>
            {renderModuleRow("English (Writing)", "Paragraph Development and Informative and Persuasive Text Type Writing")}
            {renderModuleRow("Mathematics", "Measurement and Space (Angles, Shapes and the Cartesian Plane)")}
            {renderModuleRow("Mathematics", "Number (Whole Number)")}
            {renderModuleRow("Mathematics", "Number (Fractions, Decimals and Percent)")}
            {renderModuleRow("Mathematics", "Algebra (Number Patterns)")}
            {renderModuleRow("Mathematics", "Probability and Statistics (Chance and Data)")}
            {renderModuleRow("Science", "Biological Sciences (Living Things and Habitats)")}
            {renderModuleRow("Science", "Earth and Space Sciences (Earth's Surface and Our Solar System)")}
            {renderModuleRow("Science", "Chemical Sciences (Matter and Change)")}
            {renderModuleRow("Science", "Physical Sciences (Energy: Light and Electricity)")}
            {renderModuleRow("Humanities", "Colonisation and Federation (Australia before and after 1901)")}
            {renderModuleRow("Humanities", "Economics (Needs, Wants and Opportunities)")}
          </tbody>
        </Table>
      </div>

      <h5 className="pt-3">Year 7-8</h5>
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Subject Area</th>
              <th>Year 7-8 Academic Language Module Name</th>
            </tr>
          </thead>
          <tbody>
            {renderModuleRow("English (Writing)", "Paragraph and Persuasive Essay Writing")}
            {renderModuleRow("Mathematics", "Measurement and Space (Angles, Polygons, Area and Perimeter)")}
            {renderModuleRow("Mathematics", "Number (Rational Numbers, Fractions and Decimals)")}
            {renderModuleRow("Mathematics", "Algebra (Linear Expressions and Equations)")}
            {renderModuleRow("Mathematics", "Probability and Statistics (Representing Data and Sample Space)")}
            {renderModuleRow("Science", "Biological Sciences (Biology - Classification and Cell Structure and Function)")}
            {renderModuleRow("Science", "Earth and Space Sciences (Seasons, Tides and Plate Tectonics)")}
            {renderModuleRow("Science", "Chemical Sciences (Chemistry - Particle Theory and Structure of Matter)")}
            {renderModuleRow("Science", "Physical Sciences (Physics - Forces and Energy Transformations)")}
            {renderModuleRow("Geography", "Place and Liveability, Urbanisation and Landscapes/Landforms")}
            {renderModuleRow("History", "Ancient and Medieval History")}
          </tbody>
        </Table>
      </div>

      <h5 className="pt-3">Year 9-10</h5>
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Subject Area</th>
              <th>Year 9-10 Academic Language Module Name</th>
            </tr>
          </thead>
          <tbody>
            {renderModuleRow("English (Writing)", "Paragraph and Persuasive Essay Writing")}
            {renderModuleRow("Mathematics", "Measurement and Space (Volume, Surface Area, Similarity and Trigonometry)")}
            {renderModuleRow("Mathematics", "Number (Irrational Numbers, Fractions and Decimals)")}
            {renderModuleRow("Mathematics", "Algebra (Graphing Linear and Quadratic Equations)")}
            {renderModuleRow("Mathematics", "Probability and Statistics (Tree Diagrams, Two-Way Tables, Box Plots and Scatterplots)")}
            {renderModuleRow("Science", "Biological Sciences (Biology - Body Systems and Genetic Variation)")}
            {renderModuleRow("Science", "Earth and Space Sciences (Carbon Cycle and Theory of Evolution)")}
            {renderModuleRow("Science", "Chemical Sciences (Chemistry - Subatomic Particles and Periodic Table)")}
            {renderModuleRow("Science", "Physical Sciences (Physics - Newton's Laws and Energy Transfer)")}
            {renderModuleRow("Geography", "Biomes, Environmental Change, Interconnection and Sustainability")}
            {renderModuleRow("History", "Modern History, Wars and Globalisation")}
          </tbody>
        </Table>
      </div>

      <h4 className="pt-4">Free Sample</h4>
      <hr />
      <p>View some free sample tasks from within modules here:</p>
      <iframe
        title="student-free-sample-slides"
        src="https://docs.google.com/presentation/d/e/2PACX-1vSLY2ULFxuCXgH0S6lwM5W9xsVnL0wAG8q2arP-1mfK4vNPAE7cDlC96WCHoMyJWNoa9X-P2T5Wd5Xh/pubembed?start=false&loop=false&delayms=3000"
        width="100%"
        height="880"
        allowFullScreen={true}
        mozallowfullscreen="true"
        webkitallowfullscreen="true"
      ></iframe>

      <p className="pb-5">
        Email <a href="mailto:aclangedge@gmail.com">aclangedge@gmail.com</a>
        {" "}
        to subscribe for the full suite of 11 modules for your child.
      </p>
    </Container>
  );
};

export default StudentsScreen;
